import type { TenantBranding } from "@/api/chain";

export const DEFAULT_TENANT_LOGO = '/investor/true-artis-logo-light.svg';

export function apiBaseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL || "http://localhost:3001").replace(/\/$/, "");
}

/** Origin for static `/uploads/*` files (served at site root, not under `/api`). */
export function uploadsBaseUrl(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return apiBaseUrl().replace(/\/api\/?$/, "");
}

export function resolveAssetUrl(url?: string | null): string {
  const u = (url ?? "").trim();
  if (!u) return DEFAULT_TENANT_LOGO;
  if (u.startsWith("/uploads/")) return `${uploadsBaseUrl()}${u}`;
  if (/^https?:\/\/.+\/api\/uploads\//i.test(u)) {
    return u.replace(/\/api(\/uploads\/)/i, "$1");
  }
  return u;
}

/** Resolve logo for display — prefixes API host for server-uploaded paths. */
export function resolveTenantLogo(branding?: TenantBranding | null): string {
  return resolveAssetUrl(branding?.logoUrl);
}

export const DEFAULT_BRAND_COLORS = {
  primaryColor: '#6F553D',
  secondaryColor: '#707B82',
  accentColor: '#B07A3E',
  sidebarBgColor: '#F9F6F1',
} as const;

export function emptyBrandingForm(): TenantBranding {
  return {
    logoUrl: "",
    primaryColor: DEFAULT_BRAND_COLORS.primaryColor,
    secondaryColor: DEFAULT_BRAND_COLORS.secondaryColor,
    accentColor: DEFAULT_BRAND_COLORS.accentColor,
    sidebarBgColor: DEFAULT_BRAND_COLORS.sidebarBgColor,
    customUiColors: false,
  };
}

export function brandingFromRow(
  row?: {
    logoUrl?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    accentColor?: string | null;
    sidebarBgColor?: string | null;
    customUiColors?: boolean | null;
  } | null
): TenantBranding {
  return {
    logoUrl: row?.logoUrl ?? "",
    primaryColor: row?.primaryColor ?? DEFAULT_BRAND_COLORS.primaryColor,
    secondaryColor: row?.secondaryColor ?? DEFAULT_BRAND_COLORS.secondaryColor,
    accentColor: row?.accentColor ?? DEFAULT_BRAND_COLORS.accentColor,
    sidebarBgColor: row?.sidebarBgColor ?? DEFAULT_BRAND_COLORS.sidebarBgColor,
    customUiColors: row?.customUiColors === true,
  };
}

export function brandingToPayload(
  form: TenantBranding,
  opts?: { allowNull?: boolean }
): Record<string, string | boolean | null> {
  const trim = (v: string | null | undefined) => {
    const s = (v ?? "").trim();
    if (!s && opts?.allowNull) return null;
    return s || null;
  };
  return {
    logoUrl: trim(form.logoUrl),
    primaryColor: trim(form.primaryColor),
    secondaryColor: trim(form.secondaryColor),
    accentColor: trim(form.accentColor),
    sidebarBgColor: trim(form.sidebarBgColor),
    customUiColors: form.customUiColors,
  };
}

function resolvedColors(branding: TenantBranding) {
  if (!branding.customUiColors) {
    return { ...DEFAULT_BRAND_COLORS };
  }
  return {
    primaryColor: branding.primaryColor?.trim() || DEFAULT_BRAND_COLORS.primaryColor,
    secondaryColor: branding.secondaryColor?.trim() || DEFAULT_BRAND_COLORS.secondaryColor,
    accentColor: branding.accentColor?.trim() || DEFAULT_BRAND_COLORS.accentColor,
    sidebarBgColor: branding.sidebarBgColor?.trim() || DEFAULT_BRAND_COLORS.sidebarBgColor,
  };
}

export function applyBrandingCssVars(branding: TenantBranding | null | undefined) {
  const root = document.documentElement;
  if (!branding) {
    root.style.removeProperty("--brand-primary");
    root.style.removeProperty("--brand-secondary");
    root.style.removeProperty("--brand-accent");
    root.style.removeProperty("--brand-sidebar-bg");
    root.style.removeProperty("--brand-nav-active-bg");
    return;
  }
  const colors = resolvedColors(branding);
  root.style.setProperty("--brand-primary", colors.primaryColor);
  root.style.setProperty("--brand-secondary", colors.secondaryColor);
  root.style.setProperty("--brand-accent", colors.accentColor);
  root.style.setProperty("--brand-sidebar-bg", colors.sidebarBgColor);
  root.style.setProperty(
    "--brand-nav-active-bg",
    `color-mix(in srgb, ${colors.primaryColor} 12%, white)`
  );
}
